/**
 * Bar Council enrollment number rules.
 *
 * Every State Bar Council (one per High Court jurisdiction) issues enrollment
 * numbers in its own style, e.g.
 *   Uttar Pradesh  UP10464/22, UP/10464/2022
 *   Delhi          D/1234/2015, D-1234/2015
 *   Maharashtra    MAH/1234/2010, MH/2024/123456
 *   Karnataka      KAR/1234/2015
 *   Tamil Nadu     MS/1234/2015, 1234/2015
 *
 * The prefix identifies the State Bar Council; the rest is a serial number
 * (optionally with a letter suffix) and the year of enrollment, either as
 * SERIAL/YEAR (most states) or YEAR/SERIAL (newer online enrollments).
 *
 * Keep in sync with backend/src/utils/enrollment.ts.
 */

export interface BarCouncil {
  state: string;
  highCourt: string;
  prefixes: string[];
  example: string;
}

export const BAR_COUNCILS: BarCouncil[] = [
  { state: "Uttar Pradesh", highCourt: "Allahabad", prefixes: ["UP"], example: "UP10464/22" },
  { state: "Delhi", highCourt: "Delhi", prefixes: ["D", "DL", "DEL"], example: "D/1234/2015" },
  { state: "Maharashtra & Goa", highCourt: "Bombay", prefixes: ["MAH", "MH", "MA", "GOA"], example: "MAH/1234/2010" },
  { state: "Karnataka", highCourt: "Karnataka", prefixes: ["KAR", "KA", "KRN"], example: "KAR/1234/2015" },
  { state: "Tamil Nadu & Puducherry", highCourt: "Madras", prefixes: ["MS", "TN", "TNP", "PY"], example: "MS/1234/2015" },
  { state: "Kerala", highCourt: "Kerala", prefixes: ["K", "KL", "KER"], example: "K/1234/2015" },
  { state: "West Bengal", highCourt: "Calcutta", prefixes: ["WB", "F"], example: "WB/1234/2015" },
  { state: "Gujarat", highCourt: "Gujarat", prefixes: ["G", "GJ", "GUJ"], example: "G/1234/2015" },
  { state: "Rajasthan", highCourt: "Rajasthan", prefixes: ["R", "RJ", "RAJ"], example: "R/1234/2015" },
  { state: "Madhya Pradesh", highCourt: "Madhya Pradesh", prefixes: ["MP"], example: "MP/1234/2015" },
  { state: "Punjab & Haryana", highCourt: "Punjab & Haryana", prefixes: ["P", "PH", "PB", "HR", "CHD"], example: "P/1234/2015" },
  { state: "Andhra Pradesh", highCourt: "Andhra Pradesh", prefixes: ["AP"], example: "AP/1234/2015" },
  { state: "Telangana", highCourt: "Telangana", prefixes: ["TS", "TG"], example: "TS/1234/2015" },
  { state: "Bihar", highCourt: "Patna", prefixes: ["B", "BR", "BIH"], example: "BR/1234/2015" },
  { state: "Odisha", highCourt: "Orissa", prefixes: ["O", "OR", "OD"], example: "O-1234/2015" },
  { state: "Assam, Nagaland, Mizoram & Arunachal", highCourt: "Gauhati", prefixes: ["AS", "ASM", "NL", "MZ", "AR"], example: "AS/1234/2015" },
  { state: "Himachal Pradesh", highCourt: "Himachal Pradesh", prefixes: ["HP"], example: "HP/1234/2015" },
  { state: "Jammu & Kashmir and Ladakh", highCourt: "Jammu & Kashmir and Ladakh", prefixes: ["JK", "JKL"], example: "JK/1234/2015" },
  { state: "Jharkhand", highCourt: "Jharkhand", prefixes: ["JH", "JHR"], example: "JH/1234/2015" },
  { state: "Chhattisgarh", highCourt: "Chhattisgarh", prefixes: ["CG"], example: "CG/1234/2015" },
  { state: "Uttarakhand", highCourt: "Uttarakhand", prefixes: ["UK", "UA", "UT"], example: "UK/1234/2015" },
  { state: "Manipur", highCourt: "Manipur", prefixes: ["MN"], example: "MN/123/2015" },
  { state: "Meghalaya", highCourt: "Meghalaya", prefixes: ["ML"], example: "ML/123/2015" },
  { state: "Tripura", highCourt: "Tripura", prefixes: ["TR"], example: "TR/123/2015" },
  { state: "Sikkim", highCourt: "Sikkim", prefixes: ["SK"], example: "SK/123/2015" },
];

const COUNCIL_BY_PREFIX = new Map<string, BarCouncil>(
  BAR_COUNCILS.flatMap((council) =>
    council.prefixes.map((prefix) => [prefix, council] as const),
  ),
);

// PREFIX [sep] SERIAL[-A] sep YEAR   e.g. UP10464/22, D/1234-A/2015, O-1234/2015
const SERIAL_YEAR = /^([A-Z]{1,4})?[/\- ]?(\d{1,7}(?:-?[A-Z])?)[/-](\d{4}|\d{2})$/;
// PREFIX sep YEAR sep SERIAL          e.g. MH/2024/123456
const YEAR_SERIAL = /^([A-Z]{1,4})[/-](\d{4})[/-](\d{1,7})$/;

const isValidYear = (year: string) => {
  if (year.length === 2) return true;
  const value = Number(year);
  return value >= 1950 && value <= new Date().getFullYear();
};

export const normalizeEnrollment = (value: string) =>
  value
    .trim()
    .toUpperCase()
    .replaceAll(/\s+/g, " ")
    .replaceAll(/ ?([/-]) ?/g, "$1");

/** Returns the State Bar Council an enrollment number belongs to, if recognised. */
export const findBarCouncil = (value: string) => {
  const prefix = /^[A-Z]+/.exec(normalizeEnrollment(value))?.[0];
  return prefix ? COUNCIL_BY_PREFIX.get(prefix) : undefined;
};

/** Returns the matched prefix ("" when none), or null when the format is invalid. */
const matchFormat = (normalized: string): string | null => {
  const serialYear = SERIAL_YEAR.exec(normalized);
  if (serialYear && isValidYear(serialYear[3])) return serialYear[1] ?? "";

  const yearSerial = YEAR_SERIAL.exec(normalized);
  if (yearSerial && isValidYear(yearSerial[2])) return yearSerial[1];

  return null;
};

/** Returns an error message, or "" when the enrollment number is valid. */
export const validateEnrollment = (value: string) => {
  const normalized = normalizeEnrollment(value);
  if (!normalized) return "Enrollment number is required";

  const prefix = matchFormat(normalized);
  const council = findBarCouncil(normalized);
  if (prefix === null) {
    return council
      ? `Enter a valid ${council.state} enrollment number (e.g. ${council.example})`
      : "Enter a valid enrollment number (e.g. UP10464/22, D/1234/2015, MAH/1234/2010)";
  }

  // Some councils (e.g. Tamil Nadu, Bihar) also issue plain SERIAL/YEAR numbers.
  if (prefix && !council) {
    return `Unknown State Bar Council code "${prefix}"`;
  }
  return "";
};
